import React, { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryGroup } from 'victory';

import lang from '../../../../../lib/language';
import {
  BaseStatementSearchParamType,
  StatementPeriodType,
} from '../../../../../lib/models/fundamental/types';
import { RootState } from '../../../../../lib/store';
import { getStatementsIncome } from '../../../../../lib/store/fundamental';

import { FinancialPeriodButtons } from '../../../../../components';
import { useIsMobile } from '../../../../../hooks';
import Button from '../../../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../../../../util/routes';
import { incomeChartData } from '../Financials.types';

import DataDots from './ChartCommonComponents/DataDots/DataDots';
import OptionsBar from './ChartCommonComponents/OptionsBar/OptionsBar';
import {
  barColorsEnum,
  CHART_HEIGHT,
  emptyIncomeData,
  getSvgHeightPaddingHeight,
  SVG_HEIGHT,
  xAxisStyle,
  yAxisRightStyle,
} from './Chart.constants';
import {
  formatNumber,
  getBarLocation,
  getBarWidth,
  getChartDomainLength,
  getChartOffset,
  getChartWidth,
  getResultsCount,
} from './helpers';

import styles from './Chart.module.scss';

type Props = {
  symbol: string;
} & ReduxProps;

const IncomeChart = (props: Props) => {
  const { symbol, getStatements, statementsData } = props;
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const RESULTS_COUNT = getResultsCount(isMobile);

  const [selectedOption, setSelectedOption] = useState<number>(0);
  const [period, setPeriod] = useState<StatementPeriodType>(StatementPeriodType.ANNUAL);
  const [data, setData] = useState<incomeChartData[]>([]);
  const [domainLength, setDomainLength] = useState<number>(0);

  useEffect(() => {
    getStatements({
      symbol,
      periodType: period,
    });
  }, [getStatements, symbol, period]);

  useEffect(() => {
    if (statementsData.income.length > 0) {
      // Fix React mutation permit by creating a new array
      const reverseData = [...statementsData.income].reverse();
      const stateData = reverseData.slice(0, RESULTS_COUNT).map((item) => ({
        revenue: item.revenue,
        netIncome: item.netIncome,
        key: item.calendarYear,
        period: item.period,
      }));

      // if the results are less than the RESULTS_COUNT, propagate empty data
      while (stateData.length < RESULTS_COUNT) {
        stateData.push(emptyIncomeData);
      }
      setData(stateData);
      setDomainLength(stateData.length + 3);
    }
  }, [statementsData, RESULTS_COUNT]);

  const cornerRadius = { topLeft: 4, topRight: 4 };
  const renderBars = (): React.ReactElement[] => {
    const bars: React.ReactElement[] = [];
    data.forEach((item, index) => {
      const barLocation = getBarLocation(isMobile, index);
      bars.push(
        <VictoryBar
          key={`${item.revenue}-${item.key}`}
          barWidth={getBarWidth(isMobile)}
          data={[{ x: barLocation, y: item.revenue }]}
          style={{ data: { fill: barColorsEnum.Primary } }}
          cornerRadius={cornerRadius}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: () => {
                  setSelectedOption(index);
                  return null;
                },
              },
            },
          ]}
        />,
      );
      bars.push(
        <VictoryBar
          key={`${item.netIncome}-${item.key}`}
          barWidth={getBarWidth(isMobile)}
          data={[{ x: barLocation + (isMobile ? 0.2 : 0.1), y: item.netIncome }]}
          style={{ data: { fill: barColorsEnum.Secondary } }}
          cornerRadius={cornerRadius}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: () => {
                  setSelectedOption(index);
                  return null;
                },
              },
            },
          ]}
        />,
      );
    });

    return bars;
  };

  return (
    <>
      <div className={styles.financialTitle}>
        <div>{lang.financialsIncomeStatement()}</div>
        <Button
          variant={ButtonTypesEnum.Link}
          className={isMobile ? styles.viewAllFinancialsMobile : styles.viewAll}
          onClick={() => navigate(`${UI_ROUTES.symbol}/${symbol}${UI_ROUTES.incomeStatement}`)}
        >
          {lang.commonViewAll()}
        </Button>
      </div>
      <div className={styles.chartContainer}>
        <div className={styles.periodButtons}>
          <FinancialPeriodButtons currentPeriod={period} setPeriod={setPeriod} />
        </div>
        {data.length > 0 ? (
          <div className={styles.chart}>
            <svg
              height={SVG_HEIGHT}
              width="100%"
              preserveAspectRatio="none"
              viewBox={`0 0 ${getChartWidth(isMobile)} ${getSvgHeightPaddingHeight(isMobile)}`}
            >
              <VictoryChart
                standalone={false}
                width={getChartWidth(isMobile)}
                height={CHART_HEIGHT}
                domain={{ x: [0, getChartDomainLength(isMobile, domainLength)] }}
              >
                <VictoryAxis style={xAxisStyle} />
                <VictoryAxis
                  dependentAxis
                  orientation="right"
                  style={yAxisRightStyle}
                  tickFormat={(tick, index) => formatNumber(tick, index)}
                />
                <VictoryGroup offset={getChartOffset(isMobile)}>{renderBars()}</VictoryGroup>
              </VictoryChart>
            </svg>
            <OptionsBar
              data={data}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              period={period}
            />
          </div>
        ) : (
          <div className={styles.emptyChart} />
        )}
        <DataDots
          titles={[`${lang.incomeRevenue()}: `, `${lang.incomeNetIncome()}: `]}
          values={[
            formatNumber(data[selectedOption]?.revenue || 0),
            formatNumber(data[selectedOption]?.netIncome || 0),
          ]}
          isLoading={!data[selectedOption]}
        />
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  statementsData: state.fundamental.statementsData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsIncome(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(IncomeChart);
